/**
 * Oberflaechen-Einstellungen (N-01 Teil 2) - synchronisiert, damit die
 * Seitenleiste auf dem Handy so aussieht wie auf dem Laptop. Die Tabelle
 * `oberflaeche` hat je Einstellung eine Zeile (`id`); die Seitenleiste ist
 * `'seitenleiste'`. D-07 (Uebersicht-Layouts) und N-05 nutzen sie spaeter mit.
 */
import { db } from '@/db/schema';
import { nowInstant } from '@/lib/datetime';
import { einstellungAus, type SeitenleistenEinstellung } from '@/domain/oberflaeche/seitenleiste';

export async function seitenleisteLaden(): Promise<SeitenleistenEinstellung> {
  return einstellungAus(await db.oberflaeche.get('seitenleiste'));
}

export async function seitenleisteSpeichern(e: SeitenleistenEinstellung): Promise<void> {
  await db.oberflaeche.put({ ...e, id: 'seitenleiste', updatedAt: nowInstant() });
}

// --- Kachel-Layouts (D-07, BE-06): `uebersicht` und `bereich:<id>` ---------

import { layoutAus, type KachelLayout } from '@/domain/oberflaeche/kacheln';
import type { FesterBereich } from '@/domain/types';

export async function kachelLayoutLaden(layoutId: string, fest?: FesterBereich): Promise<KachelLayout> {
  return layoutAus(await db.oberflaeche.get(layoutId), layoutId, fest);
}

export async function kachelLayoutSpeichern(l: KachelLayout): Promise<void> {
  await db.oberflaeche.put({ ...l, updatedAt: nowInstant() });
}

/** "Auf Standard zuruecksetzen": die Zeile entfernen, dann gilt wieder der Standard. */
export async function kachelLayoutZuruecksetzen(layoutId: string): Promise<void> {
  await db.oberflaeche.delete(layoutId);
}

// --- Eigene Zaehler (BE-06 Teil 3): Zeilen `zaehler:<id>` ---------------------

import { inDenPapierkorb } from '@/db/repositories/papierkorb';
import { ZAEHLER_PRAEFIX, zaehlerAus, type Zaehler } from '@/domain/oberflaeche/zaehler';
import { newId } from '@/lib/utils/id';
import type { ID } from '@/domain/types';

export async function zaehlerAnlegen(areaId: ID, name: string, ziel?: number): Promise<Zaehler> {
  const z: Zaehler = {
    id: `${ZAEHLER_PRAEFIX}${newId()}`, areaId, name: name.trim() || 'Zähler', wert: 0,
    ...(ziel && ziel > 0 ? { ziel } : {}), darstellung: ziel && ziel > 0 ? 'balken' : 'zahl', updatedAt: nowInstant(),
  };
  await db.oberflaeche.add(z);
  return z;
}

export async function zaehlerAendern(id: string, patch: Partial<Pick<Zaehler, 'name' | 'wert' | 'ziel' | 'darstellung'>>): Promise<void> {
  const z = zaehlerAus(await db.oberflaeche.get(id));
  if (!z) return;
  const neu: Zaehler = { ...z, ...patch, updatedAt: nowInstant() };
  if (patch.ziel === undefined && 'ziel' in patch) delete neu.ziel;
  await db.oberflaeche.put(zaehlerAus(neu) ?? z);
}

/** +1 / -1 (oder ein anderer Schritt); der Wert faellt nie unter 0. */
export async function zaehlerErhoehen(id: string, schritt = 1): Promise<void> {
  const z = zaehlerAus(await db.oberflaeche.get(id));
  if (z) await zaehlerAendern(id, { wert: Math.max(0, z.wert + schritt) });
}

export async function zaehlerLoeschen(id: string): Promise<void> {
  const z = zaehlerAus(await db.oberflaeche.get(id));
  if (!z) return;
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'oberflaeche', zeileIds: [id], titel: `Zähler ${z.name}` }, () => db.oberflaeche.delete(id));
}

// --- Eigene Statistik-Kacheln (BE-06 Teil 3b): Zeilen `statistik:<id>` ---------

import { STATISTIK_PRAEFIX, QUELLEN, statistikAus, type EigeneStatistik, type StatistikDarstellung, type StatistikQuelle } from '@/domain/oberflaeche/statistik';

export async function statistikAnlegen(areaId: ID, quelle: StatistikQuelle, darstellung: StatistikDarstellung, name?: string): Promise<EigeneStatistik> {
  const s: EigeneStatistik = { id: `${STATISTIK_PRAEFIX}${newId()}`, areaId, name: name?.trim() || QUELLEN[quelle].label, quelle, darstellung, updatedAt: nowInstant() };
  await db.oberflaeche.add(s);
  return s;
}

export async function statistikAendern(id: string, patch: Partial<Pick<EigeneStatistik, 'name' | 'quelle' | 'darstellung'>>): Promise<void> {
  const s = statistikAus(await db.oberflaeche.get(id));
  if (!s) return;
  // Hat der Name noch den Standardtext der alten Quelle, folgt er der neuen.
  const name = patch.quelle && s.name === QUELLEN[s.quelle].label ? QUELLEN[patch.quelle].label : s.name;
  await db.oberflaeche.put({ ...s, name, ...patch, updatedAt: nowInstant() } as EigeneStatistik);
}

export async function statistikLoeschen(id: string): Promise<void> {
  const s = statistikAus(await db.oberflaeche.get(id));
  if (!s) return;
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'oberflaeche', zeileIds: [id], titel: `Statistik ${s.name}` }, () => db.oberflaeche.delete(id));
}
